import { IS_PRODUCTION } from "@/features/config";
import {
  FEATURE_FLAG_KEYS,
  type FeatureFlags,
} from "./featureFlags";

const STORAGE_KEY = "paperclip:feature-flag-overrides";

const isFeatureFlagKey = (key: string): key is keyof FeatureFlags =>
  FEATURE_FLAG_KEYS.includes(key as keyof FeatureFlags);

const parseOverrides = (raw: string): Partial<FeatureFlags> => {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    const overrides: Partial<FeatureFlags> = {};
    for (const key of FEATURE_FLAG_KEYS) {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === "boolean") {
        overrides[key] = value;
      }
    }
    return overrides;
  } catch {
    return {};
  }
};

export const readFeatureFlagOverrides = (): Partial<FeatureFlags> => {
  if (IS_PRODUCTION || typeof window === "undefined") {
    return {};
  }

  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return {};
  }

  return parseOverrides(raw);
};

export const writeFeatureFlagOverrides = (overrides: Partial<FeatureFlags>): void => {
  if (IS_PRODUCTION || typeof window === "undefined") {
    return;
  }

  const sanitized: Partial<FeatureFlags> = {};
  for (const [key, value] of Object.entries(overrides)) {
    if (isFeatureFlagKey(key) && typeof value === "boolean") {
      sanitized[key] = value;
    }
  }

  if (Object.keys(sanitized).length === 0) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized));
};

export const applyFeatureFlagOverrides = (flags: FeatureFlags): FeatureFlags => {
  if (IS_PRODUCTION || typeof window === "undefined") {
    return flags;
  }

  const overrides = readFeatureFlagOverrides();
  return { ...flags, ...overrides };
};
